import { ReactNode, useEffect, useState } from 'react'

import { ISelectOption } from '$/types'
import { useAppDispatch, useAppSelector } from 'app/hooks'
import { useUpdateQRAScenarioMutation } from 'app/services/api/qra/assessmentScenarios'

import EditScenarioView from './EditScenarioView'
import {
  IQRAScenario,
  IRuleWithId,
  ScenarioOptions,
  WISViewMode
} from './types'
import { useOnEditValidator } from './utils/useOnEditWISValidator'
import { selectScenario } from './whatIfScenarioSelectors'
import { setSelectedScenarioSync } from './whatIfScenarioSlice'
import WISModal from './WISModal'

export type ScenarioEditWrapperType = {
  setViewMode: React.Dispatch<React.SetStateAction<WISViewMode>>
  scenarioOptions: ScenarioOptions
  isModalOpen: boolean
  closeModal: () => void
  canConfirm: boolean
  applyToScenario: (
    applyScenarioCallback: (scenario: IQRAScenario) => void
  ) => void
  selectedThreat: ISelectOption
  cancelButton: ReactNode
  scenarioName: string
  scenarioRules: IRuleWithId[]
  setScenarioRules: React.Dispatch<React.SetStateAction<IRuleWithId[]>>
  setScenarioName: React.Dispatch<React.SetStateAction<string>>
  setSelectedThreat: React.Dispatch<React.SetStateAction<ISelectOption>>
}

const EditScenarioWrapper = ({
  setViewMode,
  scenarioOptions,
  closeModal,
  isModalOpen,
  canConfirm,
  applyToScenario,
  selectedThreat,
  cancelButton,
  scenarioName,
  setScenarioName,
  scenarioRules,
  setScenarioRules,
  setSelectedThreat
}: ScenarioEditWrapperType) => {
  const dispatch = useAppDispatch()

  const [isScenarioNameEmpty, setIsScenarioNameEmpty] = useState<boolean>(false)
  const scenario = useAppSelector(selectScenario)

  const [
    updateScenario,
    { isLoading: isEditScenario, isSuccess: scenarioEditSuccess }
  ] = useUpdateQRAScenarioMutation()

  useEffect(() => {
    if (scenarioEditSuccess) {
      setViewMode('run')
    }
  }, [setViewMode, scenarioEditSuccess])

  const filteredScenarioNames = scenarioOptions
    .filter((option) => option.label !== '')
    .map((option) => option.label)

  const [isScenarioNameValid, scenarioNameValidationMessage] =
    useOnEditValidator(
      scenarioName,
      filteredScenarioNames,
      scenario.name,
      isScenarioNameEmpty
    )

  const handleCancel = () => {
    setViewMode('run')
  }

  const onCloseModal = () => {
    closeModal()
    dispatch(setSelectedScenarioSync({ id: 0, name: '', rules: [] }))
    setViewMode('run')
  }

  const handleUpdateScenario = (scenario: IQRAScenario) => {
    updateScenario(scenario)
    dispatch(setSelectedScenarioSync(scenario))
  }

  const canConfirmEditScenario = canConfirm && isScenarioNameValid

  return (
    <WISModal
      cancelButton={cancelButton}
      isModalOpen={isModalOpen}
      onCancel={handleCancel}
      closeModal={onCloseModal}
      canConfirm={canConfirmEditScenario}
      onConfirm={() => applyToScenario(handleUpdateScenario)}
      footerApplyLabel='Save & Apply'
      isLoading={isEditScenario}
      showRunConfirm={false}
    >
      <EditScenarioView
        scenario={scenario}
        scenarioName={scenarioName}
        setIsScenarioNameEmpty={setIsScenarioNameEmpty}
        scenarioNameValidationMessage={scenarioNameValidationMessage}
        scenarioModaltitle='Edit Scenario'
        disableDropdownClearButtons
        setScenarioName={setScenarioName}
        scenarioRules={scenarioRules}
        setScenarioRules={setScenarioRules}
        selectedThreat={selectedThreat}
        setSelectedThreat={setSelectedThreat}
        isScenarioNameValid={isScenarioNameValid}
      />
    </WISModal>
  )
}

export default EditScenarioWrapper
